import { api } from "@/lib/api/client";
import type {
  Organisation,
  OrganisationPayload,
} from "@/features/organisations/api";

export type LicensePlan = "trial" | "standard" | "premium";
export type LicenseStatus = "active" | "suspended" | "expired";

export interface License {
  id: string;
  plan: LicensePlan;
  status: LicenseStatus;
  starts_on: string;
  expires_on: string;
  grace_days: number;
  max_organisations: number | null;
  max_users: number | null;
  notes: string;
  days_remaining: number;
  is_valid: boolean;
  in_grace: boolean;
  created_at: string;
}

export interface OrganisationBrief {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
}

export interface Customer {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  is_active: boolean;
  organisations_count: number;
  current_license: License | null;
  /** URL de l'application de l'abonné (https://<slug>.<domaine principal>). */
  app_url: string;
  created_at: string;
  updated_at: string;
}

/** Modules activés au niveau de l'abonné (ADR 0053), lecture seule. */
export interface CustomerModules {
  enabled: { key: string; label: string; status: "beta" | "stable" }[];
  /** Cliniques qui règlent elles-mêmes au moins un module. */
  clinic_overrides: number;
}

export interface CustomerDetail extends Customer {
  organisations: OrganisationBrief[];
  licenses: License[];
  modules: CustomerModules;
}

export interface CustomerListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Customer[];
}

export interface CustomerWritePayload {
  name: string;
  slug: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  is_active?: boolean;
}

export interface LicenseRenewPayload {
  expires_on: string;
  plan?: LicensePlan;
  starts_on?: string;
  grace_days?: number;
  max_organisations?: number | null;
  max_users?: number | null;
  notes?: string;
}

export interface CustomerStats {
  clinics: number;
  patients: number;
  users: number;
  sales: number;
  total_invoiced: string;
  total_paid: string;
  outstanding: string;
}

export interface TopCustomer {
  id: string;
  name: string;
  slug: string;
  patients: number;
  sales: number;
  total_paid: string;
}

export interface PlatformOverview {
  customers: number;
  active_customers: number;
  licenses_total: number;
  expiring_soon: Array<{ slug: string; expires_on: string }>;
  expired: string[];
  top_customers: TopCustomer[];
}

export type LicenseState = "active" | "expiring" | "expired";

export interface LicenseRow {
  customer_id: string;
  customer_name: string;
  customer_slug: string;
  plan: LicensePlan;
  status: LicenseStatus;
  state: LicenseState;
  expires_on: string;
  days_remaining: number;
  in_grace: boolean;
}

export interface OnboardingPayload {
  customer_name: string;
  customer_slug: string;
  contact_email?: string;
  contact_phone?: string;
  customer_address?: string;
  plan?: LicensePlan;
  expires_on: string;
  grace_days?: number;
  clinic_name: string;
  clinic_slug: string;
  patient_code_prefix: string;
  admin_full_name: string;
  admin_email: string;
  admin_password: string;
}

/** Niveau console du personnel éditeur (3 paliers). */
export type PlatformRole = "super" | "manager" | "account_manager";

export const PLATFORM_ROLE_LABELS: Record<PlatformRole, string> = {
  super: "Super",
  manager: "Gérant",
  account_manager: "Gestionnaire de comptes",
};

export interface PlatformStaff {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  platform_role: PlatformRole | null;
  date_joined: string;
  last_login: string | null;
}

/** Utilisateur inscrit dans l'espace (base tenant) d'un abonné. */
export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  is_active: boolean;
  is_admin: boolean;
  is_locked: boolean;
  mfa_enabled: boolean;
  last_login: string | null;
  date_joined: string;
}

export interface TenantUsersResponse {
  customer: string;
  count: number;
  results: TenantUser[];
}

export interface PlatformStaffListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PlatformStaff[];
}

export interface PlatformStaffCreatePayload {
  full_name: string;
  email: string;
  password: string;
  platform_role?: PlatformRole;
}

/** Création/édition d'une clinique d'un abonné : payload complet du modèle
 *  Organisation (mêmes champs que le formulaire /admin/organisations), routé
 *  par la console vers la base tenant du client. */
export type ClinicCreatePayload = OrganisationPayload;

export interface ClinicCreateResponse extends CustomerDetail {
  organisation: Organisation;
}

/** Politique licences/emails de la plateforme (SOCLE-SAAS-GTS C6). */
export interface LicensePolicy {
  trial_days: number;
  grace_days: number;
  reminder_offsets: number[];
  auto_suspend_enabled: boolean;
  auto_reactivate_enabled: boolean;
  /** null = valeur par défaut du serveur. */
  email_link_hours: number | null;
  updated_at: string | null;
}

export type LicensePolicyPayload = Partial<Omit<LicensePolicy, "updated_at">>;

export interface CustomerFilters {
  search?: string;
  is_active?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

/** Domaine racine SaaS (SOCLE-SAAS-GTS C8), géré depuis la console. */
export interface PlatformDomain {
  id: string;
  name: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
}

export interface SmsPackageRow {
  id: string;
  name: string;
  description: string;
  sms_quantity: number;
  price: string;
  currency: string;
  is_active: boolean;
  is_featured: boolean;
  display_order: number;
  unit_price: string | null;
  sold_count: number;
  /** Abonnés auxquels le pack est réservé. Vide = offert à tous. */
  customers: string[];
  customer_names: string[];
  created_at: string;
}

export interface SmsPackagePayload {
  name: string;
  description?: string;
  sms_quantity: number;
  price: string;
  is_active?: boolean;
  is_featured?: boolean;
  display_order?: number;
  /** Identifiants d'abonnés. Omis ou vide : pack offert à tous. */
  customers?: string[];
}

export interface PlatformSmsSaleRow {
  id: string;
  reference: string;
  customer_name: string;
  organisation_name: string;
  package_name: string;
  sms_quantity: number;
  amount: string;
  currency: string;
  status: "pending" | "paid" | "failed" | "cancelled" | "refunded";
  payment_method: string;
  paid_at: string | null;
  credited_at: string | null;
  failure_reason: string;
  created_at: string;
}

export interface PlatformSmsSales {
  results: PlatformSmsSaleRow[];
  totals: {
    paid_amount: string;
    sms_delivered: number;
    paid_count: number;
    failed_count: number;
  };
}

export const platformApi = {
  list: async (
    filters: CustomerFilters = {},
  ): Promise<CustomerListResponse> => {
    const { data } = await api.get<CustomerListResponse>(
      "/platform/customers/",
      {
        params: filters,
      },
    );
    return data;
  },
  get: async (id: string): Promise<CustomerDetail> => {
    const { data } = await api.get<CustomerDetail>(
      `/platform/customers/${id}/`,
    );
    return data;
  },
  /** Accès support à l'espace d'un client : renvoie l'URL de handoff (ticket
   *  signé) qui connecte le staff en tant qu'admin de la clinique. */
  supportAccess: async (id: string): Promise<{ handoff_url: string }> => {
    const { data } = await api.post<{ handoff_url: string }>(
      `/platform/customers/${id}/support-access/`,
      {},
    );
    return data;
  },
  stats: async (id: string): Promise<CustomerStats> => {
    const { data } = await api.get<CustomerStats>(
      `/platform/customers/${id}/stats/`,
    );
    return data;
  },
  create: async (payload: CustomerWritePayload): Promise<Customer> => {
    const { data } = await api.post<Customer>("/platform/customers/", payload);
    return data;
  },
  update: async (
    id: string,
    payload: Partial<CustomerWritePayload>,
  ): Promise<Customer> => {
    const { data } = await api.patch<Customer>(
      `/platform/customers/${id}/`,
      payload,
    );
    return data;
  },
  renew: async (
    id: string,
    payload: LicenseRenewPayload,
  ): Promise<CustomerDetail> => {
    const { data } = await api.post<CustomerDetail>(
      `/platform/customers/${id}/renew/`,
      payload,
    );
    return data;
  },
  /** Retire un doublon de licence (soft delete). Refusé sur celle en vigueur. */
  retireLicense: async (
    id: string,
    licenseId: string,
  ): Promise<CustomerDetail> => {
    const { data } = await api.post<CustomerDetail>(
      `/platform/customers/${id}/licenses/${licenseId}/retire/`,
      {},
    );
    return data;
  },
  suspend: async (id: string): Promise<CustomerDetail> => {
    const { data } = await api.post<CustomerDetail>(
      `/platform/customers/${id}/suspend/`,
      {},
    );
    return data;
  },
  /** Suppression définitive — ADR 0035. Réservé à un abonné vierge (aucune
   *  donnée métier dans sa base) : 400 `customer_not_empty` + `findings`
   *  sinon. `force` (addendum ADR 0035) passe outre — jamais le premier
   *  essai, l'appelant doit avoir déjà vu les `findings` du refus. */
  purge: async (id: string, confirmSlug: string, force = false): Promise<void> => {
    await api.post(`/platform/customers/${id}/purge/`, {
      confirm_slug: confirmSlug,
      force,
    });
  },
  addClinic: async (
    id: string,
    payload: ClinicCreatePayload,
  ): Promise<ClinicCreateResponse> => {
    const { data } = await api.post<ClinicCreateResponse>(
      `/platform/customers/${id}/clinics/`,
      payload,
    );
    return data;
  },
  getClinic: async (id: string, orgId: string): Promise<Organisation> => {
    const { data } = await api.get<Organisation>(
      `/platform/customers/${id}/clinics/${orgId}/`,
    );
    return data;
  },
  updateClinic: async (
    id: string,
    orgId: string,
    payload: Partial<OrganisationPayload>,
  ): Promise<Organisation> => {
    const { data } = await api.patch<Organisation>(
      `/platform/customers/${id}/clinics/${orgId}/`,
      payload,
    );
    return data;
  },
  applyClinicsSettings: async (
    id: string,
    settings: { primary_color?: string; stock_tracking?: boolean },
  ): Promise<{ updated: number }> => {
    const { data } = await api.post<{ updated: number }>(
      `/platform/customers/${id}/clinics-settings/`,
      settings,
    );
    return data;
  },
  uploadClinicLogo: async (
    id: string,
    orgId: string,
    file: File,
  ): Promise<Organisation> => {
    const form = new FormData();
    form.append("logo", file);
    const { data } = await api.post<Organisation>(
      `/platform/customers/${id}/clinics/${orgId}/logo/`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  setClinicActive: async (
    id: string,
    orgId: string,
    isActive: boolean,
  ): Promise<CustomerDetail> => {
    const { data } = await api.post<CustomerDetail>(
      `/platform/customers/${id}/clinics/${orgId}/set-active/`,
      { is_active: isActive },
    );
    return data;
  },
  uploadLogo: async (id: string, file: File): Promise<CustomerDetail> => {
    const form = new FormData();
    form.append("logo", file);
    const { data } = await api.post<CustomerDetail>(
      `/platform/customers/${id}/logo/`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  licensePolicy: async (): Promise<LicensePolicy> => {
    const { data } = await api.get<LicensePolicy>("/platform/license-policy/");
    return data;
  },
  updateLicensePolicy: async (
    payload: LicensePolicyPayload,
  ): Promise<LicensePolicy> => {
    const { data } = await api.patch<LicensePolicy>(
      "/platform/license-policy/",
      payload,
    );
    return data;
  },
  overview: async (): Promise<PlatformOverview> => {
    const { data } = await api.get<PlatformOverview>("/platform/overview/");
    return data;
  },
  licenses: async (status?: LicenseState): Promise<LicenseRow[]> => {
    const { data } = await api.get<LicenseRow[]>(
      "/platform/customers/licenses/",
      {
        params: status ? { status } : {},
      },
    );
    return data;
  },
  onboard: async (payload: OnboardingPayload): Promise<CustomerDetail> => {
    const { data } = await api.post<CustomerDetail>(
      "/platform/customers/onboarding/",
      payload,
    );
    return data;
  },
  listStaff: async (): Promise<PlatformStaffListResponse> => {
    const { data } =
      await api.get<PlatformStaffListResponse>("/platform/staff/");
    return data;
  },
  createStaff: async (
    payload: PlatformStaffCreatePayload,
  ): Promise<PlatformStaff> => {
    const { data } = await api.post<PlatformStaff>("/platform/staff/", payload);
    return data;
  },
  revokeStaff: async (id: string): Promise<PlatformStaff> => {
    const { data } = await api.post<PlatformStaff>(
      `/platform/staff/${id}/revoke/`,
      {},
    );
    return data;
  },
  setStaffRole: async (
    id: string,
    role: PlatformRole,
  ): Promise<PlatformStaff> => {
    const { data } = await api.post<PlatformStaff>(
      `/platform/staff/${id}/set-role/`,
      {
        platform_role: role,
      },
    );
    return data;
  },
  // — Utilisateurs d'un espace client (base tenant) —
  tenantUsers: async (customerId: string): Promise<TenantUsersResponse> => {
    const { data } = await api.get<TenantUsersResponse>(
      `/platform/customers/${customerId}/users/`,
    );
    return data;
  },
  setTenantUserActive: async (
    customerId: string,
    userId: string,
    isActive: boolean,
  ): Promise<void> => {
    await api.post(
      `/platform/customers/${customerId}/users/${userId}/set-active/`,
      {
        is_active: isActive,
      },
    );
  },
  resetTenantUserPassword: async (
    customerId: string,
    userId: string,
  ): Promise<void> => {
    await api.post(
      `/platform/customers/${customerId}/users/${userId}/reset-password/`,
      {},
    );
  },
  messageTenantUser: async (
    customerId: string,
    userId: string,
    subject: string,
    body: string,
  ): Promise<void> => {
    await api.post(
      `/platform/customers/${customerId}/users/${userId}/message/`,
      {
        subject,
        body,
      },
    );
  },
  // C8 — domaines racine SaaS (liste non paginée, volume faible).
  listDomains: async (): Promise<PlatformDomain[]> => {
    const { data } = await api.get<PlatformDomain[]>("/platform/domains/");
    return data;
  },
  addDomain: async (name: string): Promise<PlatformDomain> => {
    const { data } = await api.post<PlatformDomain>("/platform/domains/", {
      name,
    });
    return data;
  },
  setDomainActive: async (
    id: string,
    isActive: boolean,
  ): Promise<PlatformDomain> => {
    const { data } = await api.patch<PlatformDomain>(
      `/platform/domains/${id}/`,
      {
        is_active: isActive,
      },
    );
    return data;
  },
  removeDomain: async (id: string): Promise<void> => {
    await api.delete(`/platform/domains/${id}/`);
  },
  setPrimaryDomain: async (id: string): Promise<PlatformDomain> => {
    const { data } = await api.post<PlatformDomain>(
      `/platform/domains/${id}/set-primary/`,
      {},
    );
    return data;
  },
  smsPackages: async (): Promise<SmsPackageRow[]> => {
    const { data } = await api.get<SmsPackageRow[]>("/platform/sms-packages/");
    return data;
  },
  createSmsPackage: async (payload: SmsPackagePayload): Promise<SmsPackageRow> => {
    const { data } = await api.post<SmsPackageRow>("/platform/sms-packages/", payload);
    return data;
  },
  updateSmsPackage: async (
    id: string,
    payload: Partial<SmsPackagePayload>,
  ): Promise<SmsPackageRow> => {
    const { data } = await api.patch<SmsPackageRow>(`/platform/sms-packages/${id}/`, payload);
    return data;
  },
  removeSmsPackage: async (id: string): Promise<void> => {
    await api.delete(`/platform/sms-packages/${id}/`);
  },
  smsSales: async (): Promise<PlatformSmsSales> => {
    const { data } = await api.get<PlatformSmsSales>("/platform/sms-sales/");
    return data;
  },
};

// ————————————————————————————————————————————————————————————————
// État d'exploitation (hub /configuration — catégories serveur)
// ————————————————————————————————————————————————————————————————

export interface OpsEmailStatus {
  backend: string;
  backend_label: string;
  /** Un e-mail part réellement vers un serveur distant. */
  configured: boolean;
  delivers: boolean;
  host: string;
  port: number;
  encryption: "tls" | "ssl" | "none";
  /** Identifiant masqué : reconnaissable, non rejouable. */
  auth_user: string;
  auth_password_set: boolean;
  from_address: string;
  timeout_seconds: number;
  frontend_url: string;
}

export interface OpsSmsStatus {
  backend: string;
  backend_label: string;
  delivers: boolean;
  credentials_configured: boolean;
  sender: string;
  default_country_code: string;
  dlr_secured: boolean;
}

export interface OpsPushStatus {
  enabled: boolean;
  /** `null` = parc non lisible (base indisponible). */
  active_devices: number | null;
}

export interface OpsPaymentsStatus {
  provider: string;
  enabled: boolean;
  credentials_configured: boolean;
  base_url: string;
}

export interface OpsAlertsStatus {
  relay_enabled: boolean;
  email_recipients: number;
  sms_recipients: number;
}

export interface OpsBackupsStatus {
  /** `false` = volume non monté : on ne sait pas, ce n'est pas « aucune ». */
  available: boolean;
  directory: string;
  max_age_hours: number;
  keep_days: number;
  keep_weeks: number;
  hour_utc: string;
  offsite_configured: boolean;
  last_success_at: string | null;
  age_hours: number | null;
  stale: boolean | null;
  outcome: "ok" | "failed" | "unknown";
  last_error: string;
  snapshots: number | null;
  latest_snapshot: string;
  restore_check: { outcome?: string; at?: string };
}

export interface OpsCacheStatus {
  backend: string;
  location: string;
  reachable: boolean;
}

export interface OpsWebhook {
  name: string;
  path: string;
  secured: boolean;
  guard: string;
}

export interface OpsApiStatus {
  schema_url: string;
  docs_url: string;
  /** Swagger UI charge ses scripts d'un CDN que la CSP de l'app bloque. */
  docs_blocked_by_csp: boolean;
  version: string;
  debug: boolean;
  api_keys_available: boolean;
}

export interface OpsStatus {
  email: OpsEmailStatus;
  sms: OpsSmsStatus;
  push: OpsPushStatus;
  payments: OpsPaymentsStatus;
  alerts: OpsAlertsStatus;
  backups: OpsBackupsStatus;
  cache: OpsCacheStatus;
  webhooks: OpsWebhook[];
  api: OpsApiStatus;
}

export interface OpsEmailTestResult {
  delivered: boolean;
  recipient: string;
  backend: string;
  error: string;
}

export const opsApi = {
  status: async (): Promise<OpsStatus> => {
    const { data } = await api.get<OpsStatus>("/platform/ops/status/");
    return data;
  },
  sendTestEmail: async (email?: string): Promise<OpsEmailTestResult> => {
    const { data } = await api.post<OpsEmailTestResult>("/platform/ops/email-test/", {
      ...(email ? { email } : {}),
    });
    return data;
  },
  flushCache: async (): Promise<{ cleared: boolean; error: string }> => {
    const { data } = await api.post<{ cleared: boolean; error: string }>(
      "/platform/ops/cache-flush/",
      {},
    );
    return data;
  },
};
